import bg from '@components/competitions/levels/muharram/assets/images/t0/bg4.webp';
import cityTitle from '@components/competitions/levels/muharram/assets/images/t0/city.webp';
import nameTitle from '@components/competitions/levels/muharram/assets/images/t0/name.webp';
import phoneTitle from '@components/competitions/levels/muharram/assets/images/t0/phone.webp';
import send from '@components/competitions/levels/muharram/assets/images/t0/send.webp';
import tcity from '@components/competitions/levels/muharram/assets/images/t0/tcity.webp';
import tname from '@components/competitions/levels/muharram/assets/images/t0/tname.webp';
import tphone from '@components/competitions/levels/muharram/assets/images/t0/tphone.webp';

import {appConfig} from '@config';
import {useCompetitionMuharramContext} from '@context/CompetitionMuharramContext';
import {Box, CircularProgress} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';
import React, {useCallback, useState} from 'react';
import PhoneInput, {isValidPhoneNumber} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

type IProps = {
	onDone: (userData: {phone: string; name: string; city: string}) => void;
};

type AnswerMap = {
	[day: number]: {
		[question: number]: number | null;
	};
};

type LastData = {
	competition: number;
	phone: string;
	name: string;
	city: string;
	day: number;
	answers: AnswerMap;
};

export default function UserInfoForm({onDone}: IProps) {
	const [loading, setLoading] = useState(false);
	const [phoneValid, setPhoneValid] = useState<boolean | null>(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [name, setName] = useState('');
	const [city, setCity] = useState('');
	const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
	const {
		phone,
		setPhone,
		day,
		answers,
		setMission
	} = useCompetitionMuharramContext();
	
	const sendData = useCallback(async (lastData: LastData) => {
		if (!isValidPhoneNumber(lastData.phone)) {
			setPhoneValid(false);
			return;
		}
		if (!lastData.name || !lastData.city) return;
		try {
			setLoading(true);
			setPhoneValid(true);
			
			const res = await axios.post(`${appConfig.competition2.url}muharram`, lastData);
			if (res.data.success) {
				localStorage.setItem('competitionDay', String(lastData.day));
				localStorage.setItem('muharram_user', JSON.stringify(lastData));
				onDone({phone: lastData.phone, name: lastData.name, city: lastData.city});
				setMission(0);
			} else {
				setLoading(false);
				setErrorMessage('ارسال اطلاعات با خطا مواجه شد.');
				setErrorSnackbarOpen(true); // show snackbar
			}
		} catch (err: any) {
			console.error('User info submission failed', err);
			setLoading(false);
			const serverError =
				err.response?.data?.message || 'ارسال اطلاعات با خطا مواجه شد.';
			setErrorMessage(serverError);
			setErrorSnackbarOpen(true); // show snackbar
		}
	}, [onDone]);
	
	const handleSubmit = () => {
		if (!name.trim() || !city.trim() || !phone?.trim()) {
			setErrorMessage('لطفاً همه فیلدها را کامل کنید.');
			return;
		}
		if (!isValidPhoneNumber(phone)) {
			setErrorMessage('شماره موبایل معتبر نیست.');
			return;
		}
		setErrorMessage(''); // Clear any previous error
		
		const lastData: LastData = {
			competition: 5,
			day,
			phone,
			name,
			city,
			answers
		};
		sendData(lastData);
	};
	
	return (
		<>
			<Snackbar
				open={errorSnackbarOpen}
				autoHideDuration={5000}
				onClose={() => setErrorSnackbarOpen(false)}
				anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
			>
				<MuiAlert
					onClose={() => setErrorSnackbarOpen(false)}
					severity="error"
					sx={{width: '100%'}}
				>
					{errorMessage}
				</MuiAlert>
			</Snackbar>
			<Box
				className="w-full min-h-[calc(100vh-55px)] flex items-center justify-center bg-[#004D43]"
				sx={{
					backgroundImage: `url(${bg})`,
					backgroundRepeat: 'no-repeat',
					backgroundSize: 'cover'
				}}
			>
				<div className="w-11/12 m-auto flex flex-col gap-y-10 items-center">
					{/* Name */}
					<div className={'w-full flex flex-col justify-center items-center gap-y-3'}>
						<img src={nameTitle} alt="name title" className="w-5/12"/>
						<div className="relative w-10/12 h-20">
							<img src={tname} alt="tname" className="absolute w-full h-full object-contain"/>
							<input
								type="text"
								placeholder="-----"
								value={name}
								onChange={(e) => setName(e.target.value)}
								className="absolute top-0 left-0 w-full h-full text-center bg-transparent text-white focus:outline-none"
							/>
						</div>
					</div>
					
					{/* City */}
					<div className={'w-full flex flex-col justify-center items-center gap-y-3'}>
						<img src={cityTitle} alt="city title" className="w-3/12"/>
						<div className="relative w-10/12 h-20">
							<img src={tcity} alt="tcity" className="absolute w-full h-full object-contain"/>
							<input
								type="text"
								placeholder="-----"
								value={city}
								onChange={(e) => setCity(e.target.value)}
								className="absolute top-0 left-0 w-full h-full text-center bg-transparent text-white focus:outline-none"
							/>
						</div>
					</div>
					
					{/* Phone */}
					<div className={'w-full flex flex-col justify-center items-center gap-y-3'}>
						<img src={phoneTitle} alt="phone title" className="w-4/12"/>
						<div className="relative w-10/12 h-20">
							<img src={tphone} alt="tphone" className="absolute w-full h-full object-contain"/>
							<PhoneInput
								international={false}
								placeholder="-----"
								defaultCountry="IR"
								value={phone}
								onChange={(phoneNumber) => setPhone(phoneNumber as string)}
								limitMaxLength
								countrySelectProps={{style: {display: 'none'}}}
								className="absolute top-0 left-0 w-full h-full text-center bg-transparent text-white focus:outline-none hide-flag phone-font"
							/>
						</div>
					</div>
					{errorMessage && (
						<div className="text-red-400 text-center text-sm">{errorMessage}</div>
					)}
					{!loading ? (
						<Box className="w-full flex mt-3 justify-center">
							<div className="w-7/12 h-8 flex items-center justify-center cursor-pointer">
								<img src={send} alt="send" className="w-3/6" onClick={handleSubmit}/>
							</div>
						</Box>
					) : (
						<div className="w-full flex justify-center my-10">
							<CircularProgress sx={{color: '#fff'}}/>
						</div>
					)}
				</div>
			</Box>
		</>
	);
}
